import { useEffect, useState } from "react";
import {
  Users,
  Dumbbell,
  Coffee,
  DollarSign,
  TrendingUp,
  ArrowUpRight,
  Sparkles,
  Bean,
  AlertTriangle,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DASH } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/currency";
import { Link } from "react-router-dom";

function KPI({ testid, icon: Icon, label, value, sub, tone = "default" }) {
  return (
    <div
      data-testid={testid}
      className="group rounded-xl border border-border bg-card p-6 hover:-translate-y-0.5 transition-transform"
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
        <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
      <div className="mt-4 font-display text-4xl leading-none">{value}</div>
      <div className="mt-2 text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user, tenant } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [coffeeAlerts, setCoffeeAlerts] = useState([]);

  // Gate widgets by the tenant's active modules. Solo tenants only see the
  // module they picked at signup; Studio/Empire see all.
  const modules = tenant?.modules || [];
  const hasFitness = modules.includes("fitness");
  const hasCafe = modules.includes("cafe");
  const hasRoastery = modules.includes("roastery");

  useEffect(() => {
    (async () => {
      const [k, t, u, ca] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/dashboard/revenue-trend?days=20"),
        api.get("/dashboard/upcoming"),
        hasRoastery
          ? api.get("/cafe/coffee-inventory/alerts").catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setKpis(k.data);
      setTrend(t.data);
      setUpcoming(u.data);
      setCoffeeAlerts(ca.data);
    })();
  }, [hasRoastery]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
            {tenant?.name}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">
            {greeting}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-muted-foreground mt-2 max-w-lg">
            Here's what's happening across your studio and café today.
          </p>
        </div>
        <div className="flex gap-2">
          {hasCafe && (
            <Link to="/app/cafe/pos">
              <Button variant="outline" className="rounded-full">
                <Coffee className="h-4 w-4 mr-2" /> New café order
              </Button>
            </Link>
          )}
          {hasRoastery && (
            <Link to="/app/cafe/coffee-inventory">
              <Button variant="outline" className="rounded-full">
                <Bean className="h-4 w-4 mr-2" /> Coffee inventory
              </Button>
            </Link>
          )}
          {hasFitness && (
            <Link to="/app/fitness/ai-workout">
              <Button className="rounded-full">
                <Sparkles className="h-4 w-4 mr-2" /> Generate workout
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Coffee reorder alert banner */}
      {coffeeAlerts.filter(a => a.reorder.status === "critical").length > 0 && (
        <Link
          to="/app/cafe/coffee-inventory"
          data-testid="coffee-inv-alert-banner"
          className="block mb-6 rounded-xl border border-destructive/50 bg-destructive/10 p-4 hover:bg-destructive/15 transition-colors animate-pulse-slow"
        >
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-destructive text-destructive-foreground grid place-items-center shrink-0">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-destructive">
                Reorder now — {coffeeAlerts.filter(a => a.reorder.status === "critical").length} coffee SKU{coffeeAlerts.filter(a => a.reorder.status === "critical").length > 1 ? "s" : ""} below 10-day threshold.
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {coffeeAlerts.filter(a => a.reorder.status === "critical").slice(0, 3).map(a => (
                  <span key={a.id} data-testid={`coffee-inv-alert-${a.id}`} className="mr-3">
                    <b className="text-foreground">{a.name}</b> · {a.reorder.days_remaining} days left ({a.on_hand}{a.unit_type})
                  </span>
                ))}
              </div>
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
          </div>
        </Link>
      )}
      {coffeeAlerts.filter(a => a.reorder.status === "watch").length > 0 && coffeeAlerts.filter(a => a.reorder.status === "critical").length === 0 && (
        <Link
          to="/app/cafe/coffee-inventory"
          className="block mb-6 rounded-xl border border-chart-3/40 bg-chart-3/10 p-4 hover:bg-chart-3/15 transition-colors"
        >
          <div className="flex items-start gap-3">
            <Bean className="h-5 w-5 text-chart-3 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">Watch list — {coffeeAlerts.length} coffee SKU{coffeeAlerts.length > 1 ? "s" : ""} approaching reorder.</div>
              <div className="text-xs text-muted-foreground mt-1">Plan your next order in the coming days.</div>
            </div>
          </div>
        </Link>
      )}

      {/* KPIs — module-gated */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {hasCafe && (
          <KPI
            testid={DASH.kpiOrdersToday}
            icon={Coffee}
            label="Café orders today"
            value={kpis?.orders_today ?? "—"}
            sub={`${kpis?.orders_total ?? 0} lifetime`}
          />
        )}
        {hasCafe && (
          <KPI
            testid={DASH.kpiRevenue}
            icon={DollarSign}
            label="Café revenue"
            value={formatMoney(kpis?.revenue_total ?? 0)}
            sub="All-time"
          />
        )}
        {hasFitness && (
          <KPI
            testid={DASH.kpiClients}
            icon={Users}
            label="Active clients"
            value={kpis?.clients ?? "—"}
            sub="Fitness roster"
          />
        )}
        {hasRoastery && !hasCafe && (
          <KPI
            testid="dash-kpi-roastery-skus"
            icon={Bean}
            label="Coffee SKUs tracked"
            value={coffeeAlerts.length || "—"}
            sub="Roastery inventory"
          />
        )}
      </div>

      {/* Chart + upcoming — chart only makes sense when the tenant sells café */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {hasCafe ? (
          <div
            data-testid={DASH.chartRevenue}
            className="lg:col-span-2 rounded-xl border border-border bg-card p-6"
          >
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Café revenue · last 20 days
              </div>
              <div className="mt-1 font-display text-2xl">
                {formatMoney(trend.reduce((s, r) => s + (r.revenue || 0), 0), undefined, { maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="inline-flex items-center gap-1 text-sm text-primary">
              <TrendingUp className="h-4 w-4" /> +12% wow
            </div>
          </div>
          <div className="h-64 min-h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => v.slice(5)}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "hsl(var(--primary))" }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        ) : null}

        <div className={"rounded-xl border border-border bg-card p-6 " + (hasCafe ? "" : "lg:col-span-3")}>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Upcoming
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="space-y-4">
            {(() => {
              // Hide fitness session items for tenants that don't have the fitness module.
              const filteredUpcoming = upcoming.filter(
                (u) => u.type !== "session" || hasFitness,
              );
              return (
                <>
                  {filteredUpcoming.length === 0 && (
                    <div className="text-sm text-muted-foreground">Nothing scheduled yet.</div>
                  )}
                  {filteredUpcoming.map((u, i) => (
              <div key={i} className="flex gap-3">
                <div
                  className={
                    "h-9 w-9 shrink-0 rounded-md grid place-items-center border border-border " +
                    (u.type === "order" ? "bg-secondary" : "bg-primary/10")
                  }
                >
                  {u.type === "order" ? (
                    <Coffee className="h-4 w-4 text-primary" />
                  ) : (
                    <Users className="h-4 w-4 text-primary" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {u.title}
                    {typeof u.amount === "number" && <span className="text-primary"> — {formatMoney(u.amount)}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{u.subtitle}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
                    {u.when}
                  </div>
                </div>
              </div>
            ))}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
